import { NotificationsNone as BellIcon } from '@mui/icons-material';
import { Box, List, Typography } from '@pulse/ui';
import type { ReactNode } from 'react';

import type { Notification } from '../types/notification.types';

import { NotificationItem } from './NotificationItem';

export interface NotificationPanelContentProps {
  notifications?: Notification[];
  onMarkRead?: (id: string) => void;
  onDismiss?: (id: string) => void;
  children?: ReactNode;
  emptyState?: ReactNode;
  maxHeight?: number | string;
}

// Generic content slot for FloatingPanel — deliberately not coupled to notification
// history. Pass `children` to render anything else entirely (pending approvals, system
// alerts, dashboard widgets, ...); `notifications` only drives the default list rendering.
export function NotificationPanelContent({
  notifications = [],
  onMarkRead,
  onDismiss,
  children,
  emptyState,
  maxHeight = 'calc(100vh - 146px)',
}: NotificationPanelContentProps) {
  const defaultEmptyState = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        py: 5,
        px: 2,
      }}
    >
      <BellIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
      <Typography variant="body2" color="text.disabled">
        No notifications yet
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        overflowY: 'auto',
        maxHeight,
        bgcolor: 'background.default',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'action.selected', borderRadius: 2 },
      }}
    >
      {/* Custom slot — renders any component passed as children */}
      {children ? (
        <Box sx={{ py: 1 }}>{children}</Box>
      ) : notifications.length === 0 ? (
        (emptyState ?? defaultEmptyState)
      ) : (
        <List disablePadding>
          {notifications.map((n) => (
            <NotificationItem key={n.id} notification={n} onMarkRead={onMarkRead} onDismiss={onDismiss} />
          ))}
        </List>
      )}
    </Box>
  );
}
